export const CHAR_PX = 6.6;
export const LANE_HEIGHT = 34;
export const BAR_HEIGHT = 28;
export const LANE_GAP = 0.02;
export const BLOCK_LANE_HEIGHT = 26;

const TIMES_WIDTH = 136;
const ONE_AVATAR_WIDTH = 25;
const MIN_PAGER_PX = 16;

export function petNames(booking) {
  const names = (booking.pets || []).map((pet) => String(pet.name || "").trim()).filter((name) => name.length > 0);
  if (names.length > 0) return names;
  return [String(booking.owner || "").trim() || "Booking"];
}

export function shortLabel(names) {
  return names.length > 1 ? `${names[0]} +${names.length - 1}` : names[0];
}

export function layoutBars({ bookings, start, end, dayPx, mobile, offset = 0 }) {
  const windowSize = end - start;
  const dayWidth = dayPx || (mobile ? 50 : 18);
  const lanes = [];

  const visible = bookings
    .filter((booking) => booking.e > start && booking.s < end)
    .sort((a, b) => a.s - b.s || a.id - b.id);

  const bars = visible.map((booking) => {
    const continuesLeft = booking.s < start;
    const continuesRight = booking.e > end;
    let from = Math.max(booking.s, start);
    let to = Math.min(booking.e, end);

    const minSpan = MIN_PAGER_PX / dayWidth;
    if ((continuesLeft || continuesRight) && to - from < minSpan) {
      if (continuesLeft) {
        to = from + minSpan;
      } else {
        from = to - minSpan;
      }
    }

    const span = to - from;
    const names = petNames(booking);
    const first = names[0];
    const label = shortLabel(names);
    const fullLabel = names.join(", ");
    const continues = continuesLeft || continuesRight;

    const outsideRoom = span * dayWidth - 14 - (continuesLeft ? 14 : 0) - (continuesRight ? 14 : 0);
    const outside = outsideRoom < label.length * CHAR_PX + 2;
    const outsideWidth = (24 + label.length * CHAR_PX + 12) / dayWidth;
    const outsideOnRight = to + outsideWidth <= end;
    const laneFrom = outside && !outsideOnRight ? from - outsideWidth : from;
    const laneTo = outside && outsideOnRight ? to + outsideWidth : to;

    let lane = lanes.findIndex((laneEnd) => laneEnd <= laneFrom - LANE_GAP);
    if (lane < 0) {
      lane = lanes.length;
      lanes.push(laneTo);
    } else {
      lanes[lane] = laneTo;
    }

    const leftRadius = continuesLeft ? 0 : 6;
    const rightRadius = continuesRight ? 0 : 6;
    const left = ((from - start) / windowSize) * 100;
    const top = lane * LANE_HEIGHT + 6 + offset;

    const room = span * dayWidth - (mobile ? 18 : 22) - (continuesLeft ? 14 : 0) - (continuesRight ? 14 : 0);
    const nameWidth = fullLabel.length * CHAR_PX;
    const ownerWidth = String(booking.owner || "").length * CHAR_PX + 8;
    const shownAvatars = names.slice(0, 3);
    const avatarWidth = 18 + (shownAvatars.length - 1) * 11 + (names.length > 3 ? 22 : 0) + 6;
    const firstWidth = first.length * CHAR_PX + (names.length > 1 ? 20 : 0);

    let mode = "none";
    if (outside) mode = "none";
    else if (room >= avatarWidth + nameWidth) mode = "full";
    else if (room >= ONE_AVATAR_WIDTH + nameWidth) mode = "oneFull";
    else if (room >= ONE_AVATAR_WIDTH + firstWidth) mode = "oneFirst";
    else if (room >= firstWidth) mode = "first";
    else if (room >= 20) mode = "av1";
    else if (room >= 10) mode = "init";

    const showAvatars = ["full", "oneFull", "oneFirst", "av1"].includes(mode);
    const showName = ["full", "oneFull", "oneFirst", "first"].includes(mode);
    const showOwner = mode === "full" && !mobile && room >= avatarWidth + nameWidth + ownerWidth;
    const showTimes = showOwner && room >= avatarWidth + nameWidth + ownerWidth + TIMES_WIDTH;
    const compact = room < 20;

    return {
      id: booking.id,
      lane,
      top,
      left,
      width: `calc(${(span / windowSize) * 100}% - 2px)`,
      radius: `${leftRadius}px ${rightRadius}px ${rightRadius}px ${leftRadius}px`,
      color: booking.color,
      fg: booking.fg,
      mode,
      continuesLeft,
      continuesRight,
      showLeftChevron: continuesLeft,
      showRightChevron: continuesRight,
      showAvatars,
      avatarCount: mode === "full" ? shownAvatars.length : Math.min(1, shownAvatars.length),
      moreAvatars: mode === "full" && names.length > 3 ? `+${names.length - 3}` : "",
      showName,
      name: mode === "oneFirst" || mode === "first" ? label : fullLabel,
      showInitial: mode === "init",
      initial: fullLabel.charAt(0),
      showOwner,
      showTimes,
      chevronMargin: showTimes ? "0" : "auto",
      padding: compact ? (continues ? "0 4px" : "0 1px") : mobile ? "0 6px" : "0 7px",
      gap: compact ? (continues ? "3px" : "0") : mobile ? "5px" : "6px",
      outside,
      outsideLabel: label,
      outsideLeft: outside && outsideOnRight ? `calc(${((to - start) / windowSize) * 100}% + 2px)` : "auto",
      outsideRight: outside && !outsideOnRight ? `calc(${((end - from) / windowSize) * 100}% + 2px)` : "auto",
      tip: `${fullLabel} · ${booking.owner} · ${booking.in_full} → ${booking.out_full} · ${booking.svc}`,
    };
  });

  return { bars, laneCount: lanes.length };
}

export function groupHeight(laneCount) {
  return Math.max(1, laneCount) * LANE_HEIGHT + 8;
}

export function blockRowHeight(laneCount) {
  return laneCount * BLOCK_LANE_HEIGHT + 6;
}

export function layoutBlocks({ blocks, start, end, dayPx }) {
  const windowSize = end - start;
  const lanes = [];

  const visible = blocks
    .filter((block) => block.e > start && block.s < end)
    .sort((a, b) => a.s - b.s || a.id - b.id);

  const bars = visible.map((block) => {
    const from = Math.max(block.s, start);
    const to = Math.min(block.e, end);
    const continuesLeft = block.s < start;
    const continuesRight = block.e > end;

    let lane = lanes.findIndex((laneEnd) => laneEnd <= from);
    if (lane < 0) {
      lane = lanes.length;
      lanes.push(to);
    } else {
      lanes[lane] = to;
    }

    const room = (to - from) * dayPx - 14;
    const leftRadius = continuesLeft ? 0 : 5;
    const rightRadius = continuesRight ? 0 : 5;
    const insets = (continuesLeft ? 0 : 2) + (continuesRight ? 0 : 2);

    return {
      id: block.id,
      lane,
      top: lane * BLOCK_LANE_HEIGHT + 5,
      left: `calc(${((from - start) / windowSize) * 100}% + ${continuesLeft ? 0 : 2}px)`,
      width: `calc(${((to - from) / windowSize) * 100}% - ${insets}px)`,
      radius: `${leftRadius}px ${rightRadius}px ${rightRadius}px ${leftRadius}px`,
      showLabel: room >= 40,
      justify: room >= 40 ? "flex-start" : "center",
      padding: room < 20 ? "0 3px" : "0 6px",
      label: `${block.title} · ${block.time}`,
      tip: `${block.title} · ${block.when} · ${block.time} · ${block.scope}`,
    };
  });

  return { bars, laneCount: lanes.length };
}

export function onSite(booking, dayIndex) {
  return booking.s < dayIndex + 1 && booking.e > dayIndex;
}

export function placeAtPointer({ x, y, width, height, container, viewportHeight, offsetX = 16, offsetY = 22, margin = 8 }) {
  const left = Math.max(container.left, Math.min(x - offsetX, container.right - width));

  let top = y + offsetY;
  if (top + height > viewportHeight - margin && y - 12 - height >= margin) top = y - 12 - height;

  return { left: left - container.left, top: top - container.top };
}

export function isArriving(booking, dayIndex) {
  return Math.floor(booking.s) === dayIndex;
}

export function isDeparting(booking, dayIndex) {
  return Math.floor(booking.e) === dayIndex;
}

export function pluralize(count, singular) {
  return `${count} ${singular}${count === 1 ? "" : "s"}`;
}

export function dayStats(bookings, dayIndex) {
  const list = bookings.filter((booking) => onSite(booking, dayIndex));
  const dogs = list.reduce((sum, booking) => sum + Math.max(1, (booking.pets || []).length), 0);
  const arriving = list.filter((booking) => isArriving(booking, dayIndex)).length;
  const departing = list.filter((booking) => isDeparting(booking, dayIndex)).length;
  return { list, dogs, arriving, departing };
}

export function matchesQuery(booking, query) {
  const text = query.trim().toLowerCase();
  if (text === "") return true;
  const haystack = [...(booking.pets || []).map((pet) => pet.name), booking.owner, booking.svc].join(" ").toLowerCase();
  return haystack.includes(text);
}
